import { apiClient } from './client'
import type { ApiResponse, DownloadResult } from './client'
import type { CampaignViewDto, CampaignCreateDto, CampaignUpdateDto, CampaignCardsResponseDto } from '@/types/campaign'
import type { AnalyticsViewDto, AttributionView, RawAnalyticsViewDto } from '@/types/analytics'
import { normalizeAnalyticsView } from '@/utils/analyticsNormalize'
import type { DashboardRange } from '@/types/dashboard'

/** Build the `?useMock=&attributionView=` query string shared by the range-windowed analytics calls. */
function analyticsQuery(mock?: boolean, attributionView?: AttributionView): string {
  const params = new URLSearchParams()
  if (mock) params.set('useMock', 'true')
  if (attributionView) params.set('attributionView', attributionView)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

// Campaign API functions
export const campaignApi = {
  /**
   * Get all campaigns for a specific workspace
   */
  getCampaignsByWorkspace: (workspaceId: string): Promise<ApiResponse<CampaignViewDto[]>> =>
    apiClient.get(`/api/Campaigns/Workspace/${workspaceId}`),


  /**
   * Create a new campaign.
   * Company/workspace are in the route so the API can enforce workspace-edit capability.
   */
  createCampaign: (clientCompanyId: string, workspaceId: string, campaign: CampaignCreateDto): Promise<ApiResponse<CampaignViewDto>> =>
    apiClient.post(`/api/Campaigns/${clientCompanyId}/${workspaceId}/CreateCampaign`, campaign),

  /**
   * Update an existing campaign.
   * Company/workspace are in the route so the API can enforce workspace-edit capability.
   */
  updateCampaign: (clientCompanyId: string, workspaceId: string, campaign: CampaignUpdateDto): Promise<ApiResponse<CampaignViewDto>> =>
    apiClient.put(`/api/Campaigns/${clientCompanyId}/${workspaceId}/UpdateCampaign`, campaign),

  /**
   * Get campaign web cards (assets)
   */
  GetCampaignWebCards: (clientCompanyId: string, workspaceId: string, campaignId: string): Promise<ApiResponse<CampaignCardsResponseDto>> =>
    apiClient.get(`/api/Campaigns/${clientCompanyId}/${workspaceId}/${campaignId}/Cards`),

  /**
   * ORB-328 / ORB-373: campaign-level visitor analytics, summed across all Webs in the
   * campaign and windowed by range (current vs previous window, with delta). Follows the
   * dashboard-tile route convention (range in the path); `mock` plumbs straight through.
   * `attributionView` flips credited numbers between conversion-session and first-seen.
   */
  getAnalytics: async (workspaceId: string, campaignId: string, range: DashboardRange, mock?: boolean, attributionView?: AttributionView): Promise<ApiResponse<AnalyticsViewDto>> => {
    const url = `/api/Dashboards/${encodeURIComponent(workspaceId)}/Campaigns/${encodeURIComponent(campaignId)}/Analytics/${encodeURIComponent(range)}`
    const resp = await apiClient.get<RawAnalyticsViewDto>(`${url}${analyticsQuery(mock, attributionView)}`)
    return { ...resp, data: resp.data ? normalizeAnalyticsView(resp.data) : undefined }
  },

  // ORB-373: download a CSV export of this campaign's visitor/session data. Gated
  // server-side by the export capability (403 → handled globally).
  exportAnalytics: (workspaceId: string, campaignId: string, range: DashboardRange, mock?: boolean, attributionView?: AttributionView): Promise<DownloadResult> => {
    const url = `/api/Dashboards/${encodeURIComponent(workspaceId)}/Campaigns/${encodeURIComponent(campaignId)}/Analytics/${encodeURIComponent(range)}/Export`
    return apiClient.download(`${url}${analyticsQuery(mock, attributionView)}`)
  },
}
